/**
 * Tier 2 City SEO data.
 * Each city has: county, era/architectural notes, HOA notes, local conversion hook.
 * Real-project references are shared (Kendall + North Miami) per the launch brief.
 */

export const TIER2_CITIES = {
  hialeah: {
    slug: 'hialeah',
    city: 'Hialeah',
    county: 'Miami-Dade',
    titleSuffix: 'Hialeah, FL',
    quickAnswer:
      'Hialeah homeowners typically pay $15K–$30K for full-home impact replacement. Permits run through Miami-Dade County and require Florida Product Approvals (NOA) for hurricane zones. Most homes are CBS construction from the 1950s–1980s, which keeps install time predictable.',
    localContext: [
      'Most Hialeah homes are 1950s–1980s CBS (concrete block) construction, which simplifies impact installation versus stucco or wood-frame.',
      'Permits are pulled through Miami-Dade County and the City of Hialeah — we handle both filings.',
      'Many neighborhoods near LeJeune and West Hialeah have HOA covenants we coordinate with directly.',
    ],
    hoaNotes:
      'Hialeah Gardens and parts of West Hialeah have HOA approval requirements. We provide spec sheets and NOA documents your board needs.',
    metaDescription:
      'Impact windows in Hialeah, FL: real pricing ($15K–$40K+), permit guidance through Miami-Dade County, and HOA-ready specs. No in-home appointment required.',
  },
  doral: {
    slug: 'doral',
    city: 'Doral',
    county: 'Miami-Dade',
    titleSuffix: 'Doral, FL',
    quickAnswer:
      'Doral installs typically range $18K–$40K+. Most homes are newer (2000s+) with stucco-on-frame or CBS, and many neighborhoods are HOA-controlled. Permits go through Miami-Dade County and the City of Doral.',
    localContext: [
      'Doral has a high mix of 2000s+ master-planned communities (Doral Isles, Trails of Doral, Vintage Estates) with strict HOA approval.',
      'Most homes are stucco-on-frame or CBS — we spec accordingly so HOA boards approve quickly.',
      'Permits route through Miami-Dade County and the City of Doral; we file both.',
    ],
    hoaNotes:
      'Nearly every Doral subdivision requires HOA architectural review. We deliver an HOA-ready submission package with NOA documentation.',
    metaDescription:
      'Impact windows in Doral, FL: HOA-ready specs, Miami-Dade permits, and real install pricing ($15K–$40K+). Get a quote without a sales appointment.',
  },
  kendall: {
    slug: 'kendall',
    city: 'Kendall',
    county: 'Miami-Dade',
    titleSuffix: 'Kendall, FL',
    quickAnswer:
      'Kendall full-home replacements typically run $20K–$35K. Most homes are 1970s–1990s CBS or stucco-on-frame. Permits go through Miami-Dade County. Our recent Kendall full-home project came in at ~$28,400 with ES Windows.',
    localContext: [
      'Kendall housing is mostly 1970s–1990s single-family CBS — a good fit for direct impact replacement without reframing.',
      'The Kendall corridor (Sunset, Kendall Drive, Kendale Lakes) has predictable opening sizes that streamline the quote.',
      'Permits pulled through Miami-Dade County. We handle filings, NOA documentation, and inspection coordination.',
    ],
    hoaNotes:
      'Many Kendall communities (Kendale Lakes, Calusa, The Hammocks) have HOA covenants. We pre-package architectural submissions.',
    metaDescription:
      'Impact windows in Kendall, FL: real installed pricing (recent project ~$28,400), Miami-Dade permits handled, HOA-ready specs.',
  },
  'coral-gables': {
    slug: 'coral-gables',
    city: 'Coral Gables',
    county: 'Miami-Dade',
    titleSuffix: 'Coral Gables, FL',
    quickAnswer:
      'Coral Gables installs typically range $25K–$50K+ due to historic architectural review. Many homes are 1920s–1940s Mediterranean Revival with custom openings. Permits route through the City of Coral Gables Historic Preservation Office.',
    localContext: [
      'Coral Gables has strict architectural review — historic homes near Miracle Mile, Granada, and Riviera require HPO approval.',
      'Custom-shaped openings (arches, leaded patterns) are common; we spec products that pass HPO without compromising impact ratings.',
      'Permits filed through the City of Coral Gables (separate from Miami-Dade County). We handle both layers.',
    ],
    hoaNotes:
      'Beyond HPO, several Coral Gables enclaves (Cocoplum, Gables Estates, Sunrise Harbour) have additional HOA review. Premium spec is often required.',
    metaDescription:
      'Impact windows in Coral Gables, FL: HPO-aware specs, custom historic openings, and Miami-Dade permitting. Real pricing without a sales visit.',
  },
  'pembroke-pines': {
    slug: 'pembroke-pines',
    city: 'Pembroke Pines',
    county: 'Broward',
    titleSuffix: 'Pembroke Pines, FL',
    quickAnswer:
      'Pembroke Pines full-home installs typically run $15K–$35K. Most homes are 1990s–2000s CBS or stucco-on-frame. Permits go through Broward County and the City of Pembroke Pines.',
    localContext: [
      'Pembroke Pines is dominated by 1990s–2000s suburban subdivisions — predictable opening sizes and standard CBS construction.',
      'HOA approval is the rule, not the exception (Pembroke Falls, SilverLakes, Chapel Trail, Pembroke Isles).',
      'Permits route through Broward County and the City of Pembroke Pines. We file both and coordinate inspections.',
    ],
    hoaNotes:
      'Most Pembroke Pines neighborhoods require HOA architectural review. We pre-package frame color and glass spec sheets to clear approval fast.',
    metaDescription:
      'Impact windows in Pembroke Pines, FL: real install pricing ($15K–$40K+), Broward County permits, and HOA-ready specs for major communities.',
  },
  hollywood: {
    slug: 'hollywood',
    city: 'Hollywood',
    county: 'Broward',
    titleSuffix: 'Hollywood, FL',
    quickAnswer:
      'Hollywood installs typically range $15K–$40K+. Mix of 1950s ranch homes, 1970s condos, and newer beachfront builds. Salt-air corrosion makes coastal-grade hardware important. Permits route through Broward County.',
    localContext: [
      'Hollywood has a wide age mix — 1950s ranch homes inland, 1970s mid-rise condos along the beach, and modern beachfront builds.',
      'Coastal salt-air exposure means we recommend coastal-grade hardware and frame finishes for any home east of Federal Highway.',
      'Permits filed through Broward County and the City of Hollywood; high-rise condos add HOA / building approval steps.',
    ],
    hoaNotes:
      'Hollywood Beach condos (Diplomat, Quadomain) require building engineering approval before window changes. We coordinate with property managers directly.',
    metaDescription:
      'Impact windows in Hollywood, FL: coastal-grade specs, Broward permits, and real install pricing. No in-home sales visit required.',
  },
  'west-palm-beach': {
    slug: 'west-palm-beach',
    city: 'West Palm Beach',
    county: 'Palm Beach',
    titleSuffix: 'West Palm Beach, FL',
    quickAnswer:
      'West Palm Beach installs typically range $18K–$45K+. Mix of historic El Cid / Flamingo Park homes, mid-century ranches, and waterfront properties. Permits go through Palm Beach County and the City of West Palm Beach.',
    localContext: [
      'WPB has historic districts (El Cid, Flamingo Park, Northwood) where architectural review controls window styles — we spec accordingly.',
      'Waterfront homes (Intracoastal, Lake Worth Lagoon) need higher hurricane wind-load ratings; we default to ES Windows or premium tier here.',
      'Permits route through Palm Beach County and the City of West Palm Beach — a slightly slower turnaround than Miami-Dade, factored into our timelines.',
    ],
    hoaNotes:
      'Beyond historic districts, condo associations along Flagler Drive require building engineer sign-off. We package the documentation.',
    metaDescription:
      'Impact windows in West Palm Beach, FL: historic district specs, Palm Beach County permits, real install pricing ($15K–$40K+). Get a quote with no sales visit.',
  },
};

export const TIER2_SLUGS = Object.keys(TIER2_CITIES);
